// src/Pages/Admin/BannerAdmin.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE =  "https://fastapi-backend-657266059621.asia-northeast1.run.app";

const BannerAdmin = () => {
  // auth states
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  // banner form & list
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState({
    id: null,
    link: "",
    title: "",
    description: "",
    file: null,
    expires_at: "",
  });
  const [preview, setPreview] = useState("");

  // 1) on mount, prompt for creds (or reuse saved), set axios header & verify
  useEffect(() => {
    const doAuth = async () => {
      let token = localStorage.getItem("bannerAdminAuth");
      if (!token) {
        const user = prompt("관리자 사용자 이름을 입력하세요:");
        const pass = prompt("관리자 비밀번호를 입력하세요:");
        if (!user || !pass) {
          alert("인증이 필요합니다.");
          setLoadingAuth(false);
          setAuthenticated(false);
          return;
        }
        token = btoa(`${user}:${pass}`);
        localStorage.setItem("bannerAdminAuth", token);
      }
      axios.defaults.headers.common["Authorization"] = `Basic ${token}`;

      try {
        // test credentials
        await axios.get(`${API_BASE}/api/banners/`);
        setAuthenticated(true);
      } catch (err) {
        console.error("인증 실패:", err);
        localStorage.removeItem("bannerAdminAuth");
        alert("인증에 실패했습니다. 페이지를 새로고침해주세요.");
        setAuthenticated(false);
      } finally {
        setLoadingAuth(false);
      }
    };
    doAuth();
  }, []);

  // 2) once authed, fetch banners
  useEffect(() => {
    if (!authenticated) return;
    const fetchBanners = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/banners/`);
        setBanners(res.data);
      } catch (err) {
        console.error("배너 불러오기 실패:", err);
        alert("배너를 불러오는 중 오류가 발생했습니다.");
      }
    };
    fetchBanners();
  }, [authenticated]);

  // 3) form change handler
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  // 4) preview generation
  useEffect(() => {
    if (form.file) {
      const url = URL.createObjectURL(form.file);
      setPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    if (form.id) {
      const existing = banners.find((b) => b.id === form.id);
      setPreview(existing?.image_url || "");
    } else {
      setPreview("");
    }
  }, [form.file, form.id, banners]);

  // 5) create/update submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id && !form.file) {
      alert("이미지를 선택해주세요");
      return;
    }

    const data = new FormData();
    data.append("link", form.link);
    data.append("title", form.title);
    data.append("description", form.description);
    if (form.file) data.append("file", form.file);
    if (form.expires_at) data.append("expires_at", form.expires_at);

    try {
      if (form.id) {
        await axios.put(
          `${API_BASE}/api/banners/${form.id}`,
          data,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } else {
        await axios.post(
          `${API_BASE}/api/banners/`,
          data,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }
      setForm({ id: null, link: "", title: "", description: "", file: null, expires_at: "" });
      setPreview("");
      const res = await axios.get(`${API_BASE}/api/banners/`);
      setBanners(res.data);
    } catch (err) {
      console.error("배너 저장 실패:", err.response?.data || err);
      alert("배너 저장 중 오류가 발생했습니다");
    }
  };

  // 6) edit mode
  const handleEdit = (b) => {
    setForm({
      id: b.id,
      link: b.link,
      title: b.title,
      description: b.description,
      file: null,
      expires_at: b.expires_at ? b.expires_at.slice(0, 10) : "",
    });
  };

  // 7) delete
  const handleDelete = async (id) => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;
    try {
      await axios.delete(`${API_BASE}/api/banners/${id}`);
      setBanners((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      console.error("배너 삭제 실패:", err);
      alert("삭제 중 오류가 발생했습니다");
    }
  };

  // render loading/auth states
  if (loadingAuth) {
    return <p style={{ padding: 24 }}>관리자 인증 중…</p>;
  }
  if (!authenticated) {
    return <p style={{ padding: 24, color: "red" }}>인증에 실패했습니다. 새로고침 후 다시 시도하세요.</p>;
  }

  // main UI
  return (
    <div style={{ padding: 24, fontFamily: "Arial, sans-serif" }}>
      <h2 style={{ fontSize: 24, marginBottom: 20 }}>Banner 관리 (Admin 전용)</h2>

      <form onSubmit={handleSubmit} style={{ marginBottom: 32 }}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 4 }}>
            링크 (Instagram URL)
          </label>
          <input
            type="url"
            name="link"
            placeholder="https://instagram.com/..."
            value={form.link}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: 8,
              border: "1px solid #ccc",
              borderRadius: 4,
            }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 4 }}>제목</label>
          <input
            type="text"
            name="title"
            placeholder="배너 제목"
            value={form.title}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: 8,
              border: "1px solid #ccc",
              borderRadius: 4,
            }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 4 }}>설명 (선택)</label>
          <textarea
            name="description"
            placeholder="설명"
            value={form.description}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: 8,
              border: "1px solid #ccc",
              borderRadius: 4,
              minHeight: 60,
            }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 4 }}>이미지 업로드</label>
          <input
            type="file"
            name="file"
            accept="image/*"
            onChange={handleChange}
            style={{
              display: "block",
              padding: 4,
              border: "1px solid #ccc",
              borderRadius: 4,
              cursor: "pointer",
            }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 4 }}>만료일 (삭제 예정일)</label>
          <input
            type="date"
            name="expires_at"
            value={form.expires_at}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: 8,
              border: "1px solid #ccc",
              borderRadius: 4,
            }}
          />
        </div>

        {preview && (
          <div style={{ marginBottom: 12 }}>
            <img
              src={preview}
              alt="미리보기"
              style={{ maxWidth: "100%", borderRadius: 4 }}
            />
          </div>
        )}

        <button
          type="submit"
          style={{
            padding: "8px 16px",
            border: "none",
            borderRadius: 4,
            backgroundColor: "#007bff",
            color: "#fff",
            cursor: "pointer",
          }}
        >
          {form.id ? "수정" : "등록"}
        </button>
      </form>

      <ul style={{ listStyle: "none", padding: 0 }}>
        {banners.map((b) => (
          <li
            key={b.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginBottom: 16,
              borderBottom: "1px solid #e0e0e0",
              paddingBottom: 12,
            }}
          >
            <img
              src={b.image_url}
              alt={b.title}
              width={100}
              style={{ borderRadius: 4, objectFit: "cover" }}
            />
            <div style={{ flex: 1 }}>
              <strong style={{ display: "block", fontSize: 16 }}>
                {b.title}
              </strong>
              <p style={{ margin: 0, color: "#666" }}>{b.description}</p>
              <a
                href={b.link}
                target="_blank"
                rel="noreferrer"
                style={{ color: "#007bff" }}
              >
                Link
              </a>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              <button
                onClick={() => handleEdit(b)}
                style={{
                  padding: "4px 8px",
                  border: "none",
                  borderRadius: 4,
                  backgroundColor: "#28a745",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                수정
              </button>
              <button
                onClick={() => handleDelete(b.id)}
                style={{
                  padding: "4px 8px",
                  border: "none",
                  borderRadius: 4,
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                삭제
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default BannerAdmin;
